import React, { useState, useEffect } from 'react';
import {
  Card, Typography, Row, Col, Statistic, Spin, message, Table, Tag,
  Progress, Space, Tooltip, InputNumber, Button, Alert, Collapse, Checkbox,
  Dropdown, Menu, Modal, Select
} from 'antd';
import {
  DollarOutlined, SyncOutlined, TeamOutlined, SwapOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, RightOutlined, SettingOutlined,
  FilterOutlined, StarFilled, CloseCircleOutlined, ClockCircleOutlined,
  EyeOutlined, MoreOutlined, CheckOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { analysisService } from '../services/api';

const { Option } = Select;

const { Title, Text } = Typography;
const { Panel } = Collapse;

const statusConfig = {
  new: { color: 'default', icon: <ClockCircleOutlined />, label: 'New' },
  reviewing: { color: 'processing', icon: <ExclamationCircleOutlined />, label: 'Under Review' },
  planned: { color: 'warning', icon: <ClockCircleOutlined />, label: 'Planned' },
  implemented: { color: 'success', icon: <CheckCircleOutlined />, label: 'Implemented' },
  dismissed: { color: 'error', icon: <CloseCircleOutlined />, label: 'Dismissed' },
};

const Recommendations = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [minOverlapCount, setMinOverlapCount] = useState(3);
  const [minCoreOverlaps, setMinCoreOverlaps] = useState(3);
  const [sameCategoryOnly, setSameCategoryOnly] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [expandedRowData, setExpandedRowData] = useState({});
  const [loadingProducts, setLoadingProducts] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [quickActionModal, setQuickActionModal] = useState({ visible: false, record: null });
  const navigate = useNavigate();

  const fetchVendorOverlaps = async () => {
    try {
      setLoading(true);
      const result = await analysisService.getVendorOverlaps(
        0.7,
        minOverlapCount,
        sameCategoryOnly,
        minCoreOverlaps
      );
      setData(result);
    } catch (error) {
      console.error('Error fetching vendor overlaps:', error);
      message.error('Failed to load vendor overlap data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorOverlaps();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchVendorOverlaps();
    setRefreshing(false);
    message.success('Data refreshed');
  };

  const handleMinOverlapChange = (value) => {
    setMinOverlapCount(value);
  };

  const handleApplyFilter = () => {
    fetchVendorOverlaps();
  };

  const fetchProductOverlaps = async (vendor1Id, vendor2Id) => {
    const key = `${vendor1Id}-${vendor2Id}`;
    if (expandedRowData[key]) return;

    setLoadingProducts(prev => ({ ...prev, [key]: true }));
    try {
      const result = await analysisService.getVendorPairProducts(vendor1Id, vendor2Id);
      setExpandedRowData(prev => ({ ...prev, [key]: result }));
    } catch (error) {
      console.error('Error fetching product overlaps:', error);
      message.error('Failed to load product details');
    } finally {
      setLoadingProducts(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleRowExpand = (expanded, record) => {
    const key = `${record.vendor1.id}-${record.vendor2.id}`;
    if (expanded) {
      setExpandedRowKeys(prev => [...prev, key]);
      fetchProductOverlaps(record.vendor1.id, record.vendor2.id);
    } else {
      setExpandedRowKeys(prev => prev.filter(k => k !== key));
    }
  };

  const handleManageClick = (record) => {
    navigate(`/consolidation/${record.vendor1.id}/${record.vendor2.id}`);
  };

  const handleQuickStatusChange = async (record, newStatus) => {
    const key = `${record.vendor1.id}-${record.vendor2.id}`;
    setUpdatingStatus(prev => ({ ...prev, [key]: true }));
    try {
      await analysisService.updateConsolidation(record.vendor1.id, record.vendor2.id, {
        status: newStatus
      });
      message.success(`Status updated to ${statusConfig[newStatus]?.label || newStatus}`);
      // Update local state
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          vendor_pairs: prev.vendor_pairs.map(pair => {
            if (pair.vendor1.id === record.vendor1.id && pair.vendor2.id === record.vendor2.id) {
              return { ...pair, status: newStatus };
            }
            return pair;
          })
        };
      });
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Failed to update status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleQuickDismiss = (record) => {
    Modal.confirm({
      title: 'Dismiss Recommendation',
      content: `Are you sure you want to dismiss the consolidation opportunity between ${record.vendor1.name} and ${record.vendor2.name}?`,
      okText: 'Dismiss',
      okType: 'danger',
      onOk: () => handleQuickStatusChange(record, 'dismissed'),
    });
  };

  const handleQuickApprove = (record) => {
    setQuickActionModal({ visible: true, record });
  };

  const handleQuickApproveConfirm = async (keepVendorId) => {
    const record = quickActionModal.record;
    if (!record) return;

    const key = `${record.vendor1.id}-${record.vendor2.id}`;
    setUpdatingStatus(prev => ({ ...prev, [key]: true }));
    try {
      await analysisService.updateConsolidation(record.vendor1.id, record.vendor2.id, {
        status: 'planned',
        keep_vendor_id: keepVendorId
      });
      message.success('Marked as planned for consolidation');
      setQuickActionModal({ visible: false, record: null });
      // Update local state
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          vendor_pairs: prev.vendor_pairs.map(pair => {
            if (pair.vendor1.id === record.vendor1.id && pair.vendor2.id === record.vendor2.id) {
              return { ...pair, status: 'planned', keep_vendor_id: keepVendorId };
            }
            return pair;
          })
        };
      });
    } catch (error) {
      console.error('Error approving:', error);
      message.error('Failed to approve consolidation');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleViewVendor = (vendorId) => {
    navigate(`/client-vendors/${vendorId}`);
  };

  const getQuickActionsMenu = (record) => {
    const key = `${record.vendor1.id}-${record.vendor2.id}`;
    const isUpdating = updatingStatus[key];
    const currentStatus = record.status || 'new';

    return (
      <Menu>
        <Menu.SubMenu key="status" title="Change Status" disabled={isUpdating}>
          {Object.entries(statusConfig).map(([status, config]) => (
            <Menu.Item
              key={status}
              icon={config.icon}
              disabled={status === currentStatus}
              onClick={() => handleQuickStatusChange(record, status)}
            >
              {config.label}
            </Menu.Item>
          ))}
        </Menu.SubMenu>
        <Menu.Divider />
        <Menu.Item
          key="approve"
          icon={<CheckOutlined />}
          onClick={() => handleQuickApprove(record)}
          disabled={isUpdating || currentStatus === 'implemented'}
        >
          Plan Consolidation...
        </Menu.Item>
        <Menu.Item
          key="dismiss"
          icon={<CloseCircleOutlined />}
          danger
          onClick={() => handleQuickDismiss(record)}
          disabled={isUpdating || currentStatus === 'dismissed'}
        >
          Dismiss
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          key="vendor1"
          icon={<EyeOutlined />}
          onClick={() => handleViewVendor(record.vendor1.id)}
        >
          View {record.vendor1.name}
        </Menu.Item>
        <Menu.Item
          key="vendor2"
          icon={<EyeOutlined />}
          onClick={() => handleViewVendor(record.vendor2.id)}
        >
          View {record.vendor2.name}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          key="manage"
          icon={<SettingOutlined />}
          onClick={() => handleManageClick(record)}
        >
          Full Details...
        </Menu.Item>
      </Menu>
    );
  };

  const expandedRowRender = (record) => {
    const key = `${record.vendor1.id}-${record.vendor2.id}`;
    const productData = expandedRowData[key];
    const isLoading = loadingProducts[key];

    if (isLoading) {
      return (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spin size="small" />
          <Text type="secondary" style={{ marginLeft: 8 }}>Loading product overlaps...</Text>
        </div>
      );
    }

    if (!productData || !productData.product_pairs || productData.product_pairs.length === 0) {
      return (
        <div style={{ padding: 24 }}>
          <Text type="secondary">No product details available</Text>
        </div>
      );
    }

    const productColumns = [
      {
        title: `${record.vendor1.name} Product`,
        dataIndex: ['product1', 'name'],
        key: 'product1',
        render: (name, row) => (
          <Space direction="vertical" size={0}>
            <Text strong>{name}</Text>
            {row.product1.category && (
              <Text type="secondary" style={{ fontSize: 12 }}>{row.product1.category}</Text>
            )}
          </Space>
        ),
      },
      {
        title: `${record.vendor2.name} Product`,
        dataIndex: ['product2', 'name'],
        key: 'product2',
        render: (name, row) => (
          <Space direction="vertical" size={0}>
            <Text strong>{name}</Text>
            {row.product2.category && (
              <Text type="secondary" style={{ fontSize: 12 }}>{row.product2.category}</Text>
            )}
          </Space>
        ),
      },
      {
        title: 'Similarity',
        dataIndex: 'similarity',
        key: 'similarity',
        width: 120,
        render: (similarity) => (
          <Progress
            percent={Math.round(similarity * 100)}
            size="small"
            status={similarity > 0.85 ? "success" : "normal"}
            format={percent => `${percent}%`}
          />
        ),
      },
    ];

    return (
      <div style={{ padding: '16px 0' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>
            {productData.total_overlaps} Overlapping Products
          </Text>
          <Button
            type="primary"
            icon={<SettingOutlined />}
            onClick={() => handleManageClick(record)}
          >
            Manage Opportunity
          </Button>
        </div>
        <Table
          columns={productColumns}
          dataSource={productData.product_pairs.slice(0, 10)}
          rowKey={(row) => `${row.product1.id}-${row.product2.id}`}
          pagination={false}
          size="small"
        />
        {productData.product_pairs.length > 10 && (
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <Button type="link" onClick={() => handleManageClick(record)}>
              View all {productData.product_pairs.length} overlapping products
            </Button>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'Vendor Pair',
      key: 'vendors',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Tag color="blue">{record.vendor1.name}</Tag>
            <SwapOutlined style={{ color: '#999' }} />
            <Tag color="blue">{record.vendor2.name}</Tag>
          </Space>
          {record.vendor1.category && record.vendor2.category && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.vendor1.category} / {record.vendor2.category}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Product Overlaps',
      key: 'overlaps',
      sorter: (a, b) => a.overlap_count - b.overlap_count,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Tooltip title="Core product overlaps (specific features)">
              <Tag color={record.core_overlaps > 0 ? 'green' : 'default'}>
                <StarFilled style={{ marginRight: 4 }} />
                {record.core_overlaps} core
              </Tag>
            </Tooltip>
            <Tooltip title="Auxiliary overlaps (generic features like Reporting, Integrations)">
              <Tag color="default">
                {record.auxiliary_overlaps} auxiliary
              </Tag>
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.overlap_count} total
          </Text>
        </Space>
      ),
    },
    {
      title: 'Relevance Score',
      dataIndex: 'weighted_score',
      key: 'weighted_score',
      sorter: (a, b) => a.weighted_score - b.weighted_score,
      render: (score, record) => (
        <Tooltip title="Weighted by category specificity - higher means more meaningful overlaps">
          <Progress
            percent={Math.round(score * 100)}
            size="small"
            status={score > 0.5 ? "success" : score > 0.3 ? "normal" : "exception"}
            format={percent => `${percent}%`}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Top Categories',
      key: 'categories',
      render: (_, record) => (
        <Space wrap size={[4, 4]}>
          {(record.top_overlap_categories || []).slice(0, 3).map(([cat, count]) => (
            <Tooltip key={cat} title={`${count} overlapping products`}>
              <Tag
                color={data?.auxiliary_categories?.includes(cat) ? 'default' : 'blue'}
                style={{ fontSize: 11 }}
              >
                {cat}
              </Tag>
            </Tooltip>
          ))}
        </Space>
      ),
    },
    {
      title: 'Annual Spend',
      key: 'spend',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{record.vendor1.name}: ${record.vendor1.annual_spend.toLocaleString()}</Text>
          <Text style={{ fontSize: 12 }}>{record.vendor2.name}: ${record.vendor2.annual_spend.toLocaleString()}</Text>
        </Space>
      ),
    },
    {
      title: 'Savings',
      dataIndex: 'consolidation_savings',
      key: 'consolidation_savings',
      sorter: (a, b) => a.consolidation_savings - b.consolidation_savings,
      render: (savings) => (
        <Text strong style={{ color: '#52c41a' }}>
          ${savings.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => {
        const status = record.status || 'new';
        const config = statusConfig[status] || statusConfig.new;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_, record) => {
        const key = `${record.vendor1.id}-${record.vendor2.id}`;
        const isUpdating = updatingStatus[key];
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<RightOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleManageClick(record);
              }}
            >
              Manage
            </Button>
            <Dropdown
              overlay={getQuickActionsMenu(record)}
              trigger={['click']}
              disabled={isUpdating}
            >
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                loading={isUpdating}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  if (loading && !data) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Analyzing vendor overlaps...</div>
      </div>
    );
  }

  const { vendor_pairs = [], clusters = [], summary = {}, auxiliary_categories = [] } = data || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={2}>Vendor Consolidation Opportunities</Title>
          <Text type="secondary">
            Identify vendors with overlapping product offerings that could be consolidated
          </Text>
        </div>
        <Button
          type="primary"
          icon={<SyncOutlined spin={refreshing} />}
          onClick={handleRefresh}
          loading={refreshing}
        >
          Refresh Analysis
        </Button>
      </div>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Vendor Pairs"
              value={summary.total_overlap_pairs || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Potential Savings"
              value={summary.total_consolidation_savings || 0}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => `$${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title={<span><StarFilled style={{ color: '#52c41a', marginRight: 4 }} />Core Overlaps</span>}
              value={summary.total_core_overlaps || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Auxiliary Overlaps"
              value={summary.total_auxiliary_overlaps || 0}
              valueStyle={{ color: '#999' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Vendors Affected"
              value={summary.vendors_with_overlaps || 0}
              prefix={<SwapOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic
              title="Clusters"
              value={summary.total_clusters || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Text strong><FilterOutlined /> Filters:</Text>
          </Col>
          <Col>
            <Space>
              <Text>Min overlaps:</Text>
              <InputNumber
                min={1}
                max={100}
                value={minOverlapCount}
                onChange={handleMinOverlapChange}
                style={{ width: 70 }}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Text>Min core overlaps:</Text>
              <InputNumber
                min={0}
                max={50}
                value={minCoreOverlaps}
                onChange={setMinCoreOverlaps}
                style={{ width: 70 }}
              />
            </Space>
          </Col>
          <Col>
            <Checkbox
              checked={sameCategoryOnly}
              onChange={(e) => setSameCategoryOnly(e.target.checked)}
            >
              Same category only
            </Checkbox>
          </Col>
          <Col>
            <Button type="primary" onClick={handleApplyFilter}>
              Apply Filters
            </Button>
          </Col>
        </Row>
        {auxiliary_categories.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Auxiliary categories (generic features): {auxiliary_categories.join(', ')}
            </Text>
          </div>
        )}
      </Card>

      {/* Consolidation Groups */}
      {clusters.length > 0 && (
        <Card
          title={
            <Space>
              <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
              <span>Consolidation Groups ({clusters.length})</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Alert
            message="These vendors have significant core product overlap and could potentially be consolidated"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Collapse>
            {clusters.map((cluster, index) => (
              <Panel
                key={index}
                header={
                  <Space>
                    <Tag color="orange">{cluster.vendor_count} vendors</Tag>
                    <Text>Total Spend: ${cluster.total_spend.toLocaleString()}</Text>
                    <Text type="success">
                      Potential Savings: ${cluster.consolidation_savings.toLocaleString()}
                    </Text>
                  </Space>
                }
              >
                <Row gutter={[16, 16]}>
                  {cluster.vendors.map((vendor, vIndex) => (
                    <Col key={vIndex} xs={24} sm={12} md={8} lg={6}>
                      <Card
                        size="small"
                        style={{
                          borderColor: vIndex === 0 ? '#52c41a' : undefined,
                          borderWidth: vIndex === 0 ? 2 : 1
                        }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Space>
                            {vIndex === 0 && (
                              <Tooltip title="Recommended to keep (highest spend)">
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                              </Tooltip>
                            )}
                            <Text strong>{vendor.name}</Text>
                          </Space>
                          {vendor.category && (
                            <Tag color="blue" style={{ fontSize: 11 }}>{vendor.category}</Tag>
                          )}
                          <Statistic
                            value={vendor.annual_spend}
                            prefix="$"
                            precision={0}
                            valueStyle={{ fontSize: 16 }}
                          />
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">
                    Recommendation: Consider consolidating to <Text strong>{cluster.keep_recommendation}</Text> (highest spend vendor)
                  </Text>
                </div>
              </Panel>
            ))}
          </Collapse>
        </Card>
      )}

      {/* Vendor Pairs Table */}
      <Card title="Vendor Overlap Details">
        <Alert
          message={
            <span>
              <StarFilled style={{ color: '#52c41a', marginRight: 4 }} />
              <strong>Core overlaps</strong> are specific product features (like Payroll, CRM).
              <strong style={{ marginLeft: 8 }}>Auxiliary overlaps</strong> are generic features (like Reporting, Integrations) that most vendors have.
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={vendor_pairs}
            rowKey={(record) => `${record.vendor1.id}-${record.vendor2.id}`}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: "No vendor overlaps found. Try adjusting the filters." }}
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              onExpand: handleRowExpand,
              expandRowByClick: true,
            }}
          />
        </Spin>
      </Card>

      {/* Quick Action Modal for Planning Consolidation */}
      <Modal
        title="Plan Consolidation"
        open={quickActionModal.visible}
        onCancel={() => setQuickActionModal({ visible: false, record: null })}
        footer={null}
      >
        {quickActionModal.record && (
          <div>
            <Text style={{ display: 'block', marginBottom: 16 }}>
              Select which vendor to keep after consolidation:
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Button
                type="default"
                block
                size="large"
                onClick={() => handleQuickApproveConfirm(quickActionModal.record.vendor1.id)}
                style={{ height: 'auto', padding: '12px 16px', textAlign: 'left' }}
              >
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Space>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text strong>{quickActionModal.record.vendor1.name}</Text>
                  </Space>
                  <Text type="secondary">
                    ${quickActionModal.record.vendor1.annual_spend?.toLocaleString() || 0} annual spend
                  </Text>
                </Space>
              </Button>
              <Button
                type="default"
                block
                size="large"
                onClick={() => handleQuickApproveConfirm(quickActionModal.record.vendor2.id)}
                style={{ height: 'auto', padding: '12px 16px', textAlign: 'left' }}
              >
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Space>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text strong>{quickActionModal.record.vendor2.name}</Text>
                  </Space>
                  <Text type="secondary">
                    ${quickActionModal.record.vendor2.annual_spend?.toLocaleString() || 0} annual spend
                  </Text>
                </Space>
              </Button>
            </Space>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Button onClick={() => setQuickActionModal({ visible: false, record: null })}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Recommendations;
